import React, { useState, useEffect } from 'react';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import Alert from './caseTable/Alert';
import { SlNote } from "react-icons/sl";
import DOMPurify from 'dompurify';
import Spinner from 'react-bootstrap/Spinner';
import Toast from 'react-bootstrap/Toast';
import Task from './caseTable/Task';
import CaseTable from './caseTable/CaseTable';
import Modal from 'react-modal';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
 
// Bind modal to your app element (for accessibility)
Modal.setAppElement('#root');
 
function CaseTab({ components, caseId }) {
  const [key, setKey] = useState('Details');
  const [notes, setNotes] = useState([]);
  const [editorHtml, setEditorHtml] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [newNoteText, setNewNoteText] = useState('');
  const [noteVisibility, setNoteVisibility] = useState({});
  const [invalidations, setInvalidations] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [subject, setSubject] = useState('case fabrication');
  const [recipient, setRecipient] = useState('admin SE');
 
  const fetchNotes = async () => {
    if (!caseId) return;
 
    setIsLoading(true);
    try {
      const response = await fetch(`https://hms.backend.hiragroupcloud.com/api/cases/${caseId}/notes`);
      if (!response.ok) throw new Error('Failed to fetch notes');
 
      const data = await response.json();
      setNotes(data.notes || []);
      setEditorHtml(data.editorHtml || '');
 
      // Load visibility from localStorage if available
      const savedVisibility = JSON.parse(localStorage.getItem(`noteVisibility_${caseId}`)) || {};
 
      // Initialize visibility for all notes including editorHtml
      const initialVisibility = {};
      const invalidationData = {};
 
      // Handle editorHtml visibility (treat it like a note with a special ID)
      if (data.editorHtml) {
        initialVisibility['editorHtml'] = savedVisibility['editorHtml'] !== undefined
          ? savedVisibility['editorHtml']
          : true;
      }
 
      data.notes.forEach(note => {
        initialVisibility[note._id] = savedVisibility[note._id] !== undefined
          ? savedVisibility[note._id]
          : true;
 
        if (note.invalidatedBy) {
          invalidationData[note._id] = {
            by: note.invalidatedBy.name || 'Admin',
            at: new Date(note.invalidatedAt)
          };
        }
      });
 
      setNoteVisibility(initialVisibility);
      setInvalidations(invalidationData);
    } catch (error) {
      console.error("Error fetching notes:", error);
      setToastMessage('Failed to fetch notes');
      setShowToast(true);
    } finally {
      setIsLoading(false);
    }
  };
 
  const openModal = () => {
    setIsModalOpen(true);
  };
 
  const closeModal = () => {
    setIsModalOpen(false);
    setNewNoteText('');
  };
 
  const handleSubjectChange = (e) => {
    setSubject(e.target.value);
  };
 
  const handleRecipientChange = (e) => {
    setRecipient(e.target.value);
  };
 
  const handleEditorChange = (html) => {
    setNewNoteText(html);
  };
 
  const addNote = async () => {
    if (!newNoteText.trim()) {
      setToastMessage('Note cannot be empty');
      setShowToast(true);
      return;
    }
 
    setIsLoading(true);
    try {
      const formattedNote = `
        <div style="font-weight: bold; margin-bottom: 15px; border-bottom: 1px solid #eee; padding-bottom: 5px;">
          ${recipient} (for internal use only)<br/>
          Created: ${new Date().toLocaleString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })} for ${subject}
        </div>
        ${newNoteText}
      `;
 
      const response = await fetch(`https://hms.backend.hiragroupcloud.com/api/cases/${caseId}/notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ text: formattedNote }),
      });
 
      if (!response.ok) throw new Error('Failed to add note');
 
      const data = await response.json();
      setNotes([data.newNote, ...notes]);
      setNoteVisibility(prev => ({ ...prev, [data.newNote._id]: true }));
      setNewNoteText('');
      setToastMessage('Note added successfully');
      setShowToast(true);
      closeModal();
    } catch (error) {
      console.error("Error adding note:", error);
      setToastMessage('Failed to add note');
      setShowToast(true);
    } finally {
      setIsLoading(false);
    }
  };
 
  useEffect(() => {
    fetchNotes();
  }, [caseId]);
 
  const totalNotesCount = notes.length + (editorHtml ? 1 : 0);
 
  // ReactQuill modules and formats
  const modules = {
    toolbar: [
      [{ header: '1' }, { header: '2' }, { font: [] }],
      [{ size: [] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
      ['link'],
      ['clean'],
    ],
    clipboard: {
      matchVisual: false,
    },
  };
 
  const formats = [
    'header',
    'font',
    'size',
    'bold',
    'italic',
    'underline',
    'strike',
    'blockquote',
    'list',
    'bullet',
    'indent',
    'link',
  ];
 
  return (
    <div style={{ position: 'relative' }}>
      <Toast
        onClose={() => setShowToast(false)}
        show={showToast}
        delay={3000}
        autohide
        style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 9999,
        }}
      >
        <Toast.Body>{toastMessage}</Toast.Body>
      </Toast>
 
      <Tabs id="controlled-tab" activeKey={key} onSelect={(k) => setKey(k)} className="mb-3">
        <Tab eventKey="Details" title="Details">
          <CaseTable />
        </Tab>
 
        <Tab eventKey="Notes" title={`Notes (${totalNotesCount})`}>
          <div style={{ padding: '20px' }}>
            <h4 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <SlNote /> Notes
            </h4>
 
            {isLoading ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Spinner animation="border" />
              </div>
            ) : (
              <>
                {/* Case Details (editorHtml) */}
                {editorHtml && (
                  <div
                    style={{
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      padding: '15px',
                      marginBottom: '15px',
                      backgroundColor: '#fff',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                      position: 'relative'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      {noteVisibility['editorHtml'] ? (
                        <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(editorHtml) }} />
                      ) : (
                        <div style={{ color: '#ff0000', textAlign: 'center', flexGrow: 1 }}>
                          This note has been hidden
                        </div>
                      )}
                      <input
                        type="checkbox"
                        checked={noteVisibility['editorHtml'] || false}
                        onChange={(e) => {
                          const newVisibility = { ...noteVisibility, ['editorHtml']: e.target.checked };
                          setNoteVisibility(newVisibility);
                          localStorage.setItem(`noteVisibility_${caseId}`, JSON.stringify(newVisibility));
                        }}
                        style={{ marginLeft: '10px' }}
                      />
                    </div>
                    {noteVisibility['editorHtml'] && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px' }}>
                        <small style={{ color: '#888', fontSize: '0.85rem' }}>
                          {new Date().toLocaleString('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </small>
                      </div>
                    )}
                  </div>
                )}
 
                {/* Additional Notes */}
                {notes.length === 0 ? (
                  !editorHtml && (
                    <div style={{ padding: '20px', textAlign: 'center', border: '1px dashed #ddd', borderRadius: '5px', color: '#666' }}>
                      No notes found. Add your first note!
                    </div>
                  )
                ) : (
                  notes.map((note) => (
                    <div
                      key={note._id}
                      style={{
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        padding: '15px',
                        marginBottom: '15px',
                        backgroundColor: '#fff',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                        position: 'relative',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        {noteVisibility[note._id] ? (
                          <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(note.text) }} />
                        ) : (
                          <div style={{ color: '#ff0000', textAlign: 'center', flexGrow: 1 }}>
                            {invalidations[note._id] ? (
                              `Invalidated by: ${invalidations[note._id].by} on ${invalidations[note._id].at.toLocaleString()}`
                            ) : (
                              'This note has been invalidated'
                            )}
                          </div>
                        )}
                        <input
                          type="checkbox"
                          checked={noteVisibility[note._id] || false}
                          onChange={(e) => {
                            const newVisibility = { ...noteVisibility, [note._id]: e.target.checked };
                            setNoteVisibility(newVisibility);
                            localStorage.setItem(`noteVisibility_${caseId}`, JSON.stringify(newVisibility));
                          }}
                          style={{ marginLeft: '10px' }}
                        />
                      </div>
                      {noteVisibility[note._id] && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px' }}>
                          <small style={{ color: '#666' }}>
                            {new Date(note.createdAt).toLocaleString()}
                          </small>
                        </div>
                      )}
                    </div>
                  ))
                )}
 
                {/* Add Note Button */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '10px',
                  marginTop: '20px',
                  width: '100%'
                }}>
                  <div style={{
                    position: 'relative',
                    flexGrow: 1,
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <input
                      value={newNoteText}
                      onChange={(e) => setNewNoteText(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addNote()}
                      onClick={openModal}
                      style={{
                        padding: '10px 15px',
                        width: '100%',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '14px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        transition: 'border-color 0.3s ease',
                        cursor: 'pointer',
                        ':hover': {
                          borderColor: '#004aad',
                          boxShadow: '0 1px 5px rgba(0,74,173,0.2)'
                        }
                      }}
                      placeholder="Click to add note or type and press Enter..."
                    />
                  </div>
                 
                  <button
                    onClick={openModal}
                    style={{
                      backgroundColor: '#004aad',
                      color: 'white',
                      border: 'none',
                      padding: '10px 20px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                      whiteSpace: 'nowrap',
                      transition: 'background-color 0.3s ease',
                      ':hover': {
                        backgroundColor: '#003882'
                      }
                    }}
                  >
                    Add Note
                  </button>
                </div>
              </>
            )}
          </div>
        </Tab>
 
        <Tab eventKey="Tasks" title="Tasks">
          <div style={{ padding: '20px' }}>
            <Task />
          </div>
        </Tab>
 
        <Tab eventKey="Alert" title="Alerts">
          <Alert setActiveTab={setKey} />
        </Tab>
      </Tabs>
 
      {/* Rich Text Editor Modal */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        style={{
          content: {
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            marginRight: '-50%',
            transform: 'translate(-50%, -50%)',
            width: '800px',
            maxWidth: '90%',
            padding: '20px',
            borderRadius: '8px',
          },
          overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000,
          },
        }}
        contentLabel="Add Note Modal"
      >
        <h2 style={{ color: '#004aad', marginBottom: '20px' }}>Add Note</h2>
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
            <select
              value={recipient}
              onChange={handleRecipientChange}
              style={{
                borderRadius: '4px',
                padding: '5px',
                border: '1px solid #ccc',
              }}
            >
              <option value="admin SE">Admin SE</option>
              <option value="admin SM">Admin SM</option>
              <option value="accounts">Accounts</option>
              <option value="lab">Lab</option>
            </select>
            <select
              value={subject}
              onChange={handleSubjectChange}
              style={{
                borderRadius: '4px',
                padding: '5px',
                border: '1px solid #ccc',
              }}
            >
              <option value="case fabrication">Case Fabrication</option>
              <option value="case shipping">Case Shipping</option>
              <option value="case invoice">Case Invoice</option>
            </select>
          </div>
          <div
            style={{
              border: '1px solid #ccc',
              borderRadius: '4px',
              backgroundColor: '#fff',
              minHeight: '300px',
            }}
          >
            <ReactQuill
              theme="snow"
              value={newNoteText}
              onChange={handleEditorChange}
              modules={modules}
              formats={formats}
              placeholder="Start typing your note..."
              style={{ height: '250px' }}
            />
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <button
            onClick={closeModal}
            style={{
              backgroundColor: '#f0f0f0',
              color: '#333',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={addNote}
            style={{
              backgroundColor: '#004aad',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
            disabled={!newNoteText.trim()}
          >
            {isLoading ? <Spinner size="sm" /> : 'Save Note'}
          </button>
        </div>
      </Modal>
    </div>
  );
}
 
export default CaseTab;